// Design picker view at /designs.
// Six distinct landing-page directions, each rendered as a self-contained
// hero. CSS for each design lives under a `.dp-N-*` prefix so styles don't
// bleed into the rest of the app.

import { el } from './dom';

export function renderDesignPickerView(root: HTMLElement): void {
  const page = el('div', { class: 'dp-page', id: 'top' });

  page.append(
    el('header', { class: 'dp-page-header' },
      el('h1', { class: 'dp-page-title' }, 'Sechsundzwanzig Designs für Slot.'),
      el('p', { class: 'dp-page-lead' },
        'Live-Vorschauen — keine Mockups. Bewegung, Interaktion und Effekte sind echt. ',
        'Spring per Top-Leiste oder scroll durch.',
      ),
    ),
  );

  const variants: Array<[string, string, () => HTMLElement]> = [
    ['01', 'Editorial · Swiss',       renderDesign1],
    ['02', 'Brutalismus · Mono',      renderDesign2],
    ['03', 'Bento · Pastell',         renderDesign3],
    ['04', 'Big Type · Marquee',      renderDesign4],
    ['05', 'Warm · Skizze',           renderDesign5],
    ['06', 'Dark · Neon',             renderDesign6],
    ['07', 'Glas · Frosted',          renderDesign7],
    ['08', 'Notion · Dokument',       renderDesign8],
    ['09', 'Bauhaus · Geometrisch',   renderDesign9],
    ['10', 'Memphis · 80er',          renderDesign10],
    ['11', 'Riso · Druck',            renderDesign11],
    ['12', 'Schreibmaschine',         renderDesign12],
    ['13', 'Terminal · Phosphor',     renderDesign13],
    ['14', 'Zeitung · Letterpress',   renderDesign14],
    ['15', 'Karte · Vintage',         renderDesign15],
    ['16', 'Origami · Paper',         renderDesign16],
    ['17', 'Living Matrix · Demo',    renderDesign17],
    ['18', 'Magnetic Type',           renderDesign18],
    ['19', 'Word Carousel',           renderDesign19],
    ['20', 'Mesh · Cinematic',        renderDesign20],
    ['21', 'Heatmap Wordmark',        renderDesign21],
    ['22', 'Live Time',               renderDesign22],
    ['23', 'Conversational',          renderDesign23],
    ['24', '3D Tilt',                 renderDesign24],
    ['25', 'Variable Font · Wave',    renderDesign25],
    ['26', 'Aurora · Liquid',         renderDesign26],
  ];

  // Top-of-page jump-nav.
  const nav = el('nav', { class: 'dp-toc' });
  const tocLabel = el('span', { class: 'dp-toc-label' }, 'Springen zu:');
  nav.append(tocLabel);
  for (const [num, name] of variants) {
    nav.append(
      el('a', { href: `#dp-${num}`, class: 'dp-toc-link' },
        el('strong', {}, num),
        ' ',
        el('span', { class: 'dp-toc-name' }, name),
      ),
    );
  }
  page.append(nav);

  for (const [num, name, fn] of variants) {
    const block = el('section', { class: 'dp-block', id: `dp-${num}` });
    block.append(
      el('div', { class: 'dp-block-meta' },
        el('span', { class: 'dp-block-num' }, num),
        el('span', { class: 'dp-block-name' }, name),
        el('a', { href: '#top', class: 'dp-block-top' }, '↑ oben'),
      ),
      fn(),
    );
    page.append(block);
  }

  page.append(
    el('footer', { class: 'dp-page-footer' },
      el('p', {},
        'Sag mir die Nummer + alles, was du anders willst (Farbe, Schriftgrad, weniger laut, mehr laut, …) ',
        'und ich baue die gewählte Richtung als echte Landing Page aus.',
      ),
    ),
  );

  root.append(page);
}

// =============================================================
// Design 01 — Editorial · Swiss
// =============================================================

function renderDesign1(): HTMLElement {
  const root = el('div', { class: 'dp-1' });
  root.append(
    el('div', { class: 'dp-1-grid' },
      el('div', { class: 'dp-1-side' },
        el('span', { class: 'dp-1-num' }, '№ 01'),
        el('span', { class: 'dp-1-rule' }),
        el('p', { class: 'dp-1-side-text' },
          'Ein Werkzeug, das einen Gedanken zu Ende führt: ',
          el('em', {}, 'Wann passt es allen?'),
        ),
      ),
      el('div', { class: 'dp-1-main' },
        el('h2', { class: 'dp-1-title' },
          el('span', {}, 'Termine.'),
          el('br'),
          el('span', { class: 'dp-1-italic' }, 'Endlich'),
          ' ',
          el('span', {}, 'leise.'),
        ),
        el('p', { class: 'dp-1-tagline' },
          'gotyou : Slot ist eine ruhige App für Terminfindung. ',
          'Keine Werbung, keine Konten, keine Daten an Dritte. ',
          'Ein Klick. Ein Link. Eine Antwort.',
        ),
        el('div', { class: 'dp-1-cta-row' },
          el('a', { href: '/new', class: 'dp-1-cta' }, 'Umfrage starten ↗'),
          el('span', { class: 'dp-1-meta' }, 'Hosted in Austria · Privacy-First'),
        ),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 02 — Brutalismus · Mono
// =============================================================

function renderDesign2(): HTMLElement {
  const root = el('div', { class: 'dp-2' });
  root.append(
    el('div', { class: 'dp-2-bar' }, 'GOTYOU.AT // SLOT // V1.0 // STATUS: BEREIT'),
    el('div', { class: 'dp-2-grid' },
      el('div', { class: 'dp-2-cell dp-2-title-cell' },
        el('h2', { class: 'dp-2-title' }, 'SLOT'),
        el('p', { class: 'dp-2-sub' }, 'TERMINE OHNE KOPFWEH.'),
      ),
      el('div', { class: 'dp-2-cell dp-2-desc-cell' },
        el('p', {},
          el('strong', {}, '> '),
          'Visueller Termin-Picker. Klick und zieh. ',
          'Privacy by default. Kein Account.',
        ),
        el('a', { href: '/new', class: 'dp-2-cta' }, '[ START_SESSION ]'),
      ),
      el('div', { class: 'dp-2-cell dp-2-stat-1' },
        el('span', { class: 'dp-2-stat-label' }, 'EVENTS_STORED'),
        el('span', { class: 'dp-2-stat-value' }, '0'),
      ),
      el('div', { class: 'dp-2-cell dp-2-stat-2' },
        el('span', { class: 'dp-2-stat-label' }, 'COOKIES_SET'),
        el('span', { class: 'dp-2-stat-value' }, '0'),
      ),
      el('div', { class: 'dp-2-cell dp-2-stat-3' },
        el('span', { class: 'dp-2-stat-label' }, 'EMAILS_REQ'),
        el('span', { class: 'dp-2-stat-value' }, '0'),
      ),
      el('div', { class: 'dp-2-cell dp-2-stat-4' },
        el('span', { class: 'dp-2-stat-label' }, 'KOPFWEH_LVL'),
        el('span', { class: 'dp-2-stat-value' }, '0%'),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 03 — Bento · Pastell
// =============================================================

function renderDesign3(): HTMLElement {
  const root = el('div', { class: 'dp-3' });
  root.append(
    el('div', { class: 'dp-3-grid' },
      el('div', { class: 'dp-3-tile dp-3-hero' },
        el('span', { class: 'dp-3-eyebrow' }, '🪄 gotyou : Slot'),
        el('h2', { class: 'dp-3-title' }, 'Termine\nohne\nKopfweh.'),
        el('a', { href: '/new', class: 'dp-3-cta' }, 'Loslegen →'),
      ),
      el('div', { class: 'dp-3-tile dp-3-tile-mint' },
        el('div', { class: 'dp-3-tile-emoji' }, '🪞'),
        el('h3', {}, 'Visuelle Matrix'),
        el('p', {}, 'Klick und zieh — kein Formular.'),
      ),
      el('div', { class: 'dp-3-tile dp-3-tile-pink' },
        el('div', { class: 'dp-3-tile-emoji' }, '🔒'),
        el('h3', {}, 'Privacy-First'),
        el('p', {}, 'Kalender bleibt im Browser. Wir sehen nichts.'),
      ),
      el('div', { class: 'dp-3-tile dp-3-tile-peach' },
        el('div', { class: 'dp-3-tile-emoji' }, '⭐'),
        el('h3', {}, 'Heat-Map + Sterne'),
        el('p', {}, 'Beste Slots auf einen Blick.'),
      ),
      el('div', { class: 'dp-3-tile dp-3-tile-sky' },
        el('div', { class: 'dp-3-tile-emoji' }, '🇪🇺'),
        el('h3', {}, 'EU-Hosting'),
        el('p', {}, 'Server in Österreich. DSGVO ist der Boden.'),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 04 — Big Type · Marquee
// =============================================================

function renderDesign4(): HTMLElement {
  const root = el('div', { class: 'dp-4' });
  // Marquee row of times scrolling
  const times = ['09:00', '10:30', '13:15', '14:00', '15:45', '08:00', '11:30', '16:30', '12:00', '17:15'];
  const marquee = el('div', { class: 'dp-4-marquee' });
  // duplicate so the loop is seamless
  for (let i = 0; i < 2; i++) {
    for (const t of times) marquee.append(el('span', { class: 'dp-4-time' }, t));
  }
  root.append(
    el('div', { class: 'dp-4-marquee-wrap' }, marquee),
    el('div', { class: 'dp-4-hero' },
      el('div', { class: 'dp-4-eyebrow' }, 'gotyou : Slot'),
      el('h2', { class: 'dp-4-title' }, 'SLOT'),
      el('p', { class: 'dp-4-sub' },
        'Termine planen, ohne dass irgendwo ',
        el('s', {}, 'irgendwer irgendwas speichert'),
        '.',
      ),
      el('a', { href: '/new', class: 'dp-4-cta' }, 'Jetzt starten'),
    ),
    el('div', { class: 'dp-4-marquee-wrap dp-4-marquee-rev' }, marquee.cloneNode(true)),
  );
  return root;
}

// =============================================================
// Design 05 — Warm · Skizze
// =============================================================

function renderDesign5(): HTMLElement {
  const root = el('div', { class: 'dp-5' });
  root.append(
    el('div', { class: 'dp-5-stamp' }, '✓ ohne Drama'),
    el('div', { class: 'dp-5-content' },
      el('p', { class: 'dp-5-eyebrow' }, '— gotyou präsentiert —'),
      el('h2', { class: 'dp-5-title' },
        'Slot.',
        el('span', { class: 'dp-5-underline' }, ''),
      ),
      el('p', { class: 'dp-5-tagline' },
        'Du fragst sechs Leute nach einem Termin. ',
        'Vier antworten. Du findest trotzdem einen. ',
        el('br'),
        el('em', {}, 'So ungefähr soll Software sein.'),
      ),
      el('div', { class: 'dp-5-cta-row' },
        el('a', { href: '/new', class: 'dp-5-cta' }, 'Umfrage starten'),
      ),
      el('div', { class: 'dp-5-doodles' },
        el('span', { class: 'dp-5-arrow' }, '↗'),
        el('span', { class: 'dp-5-circle' }, '○'),
        el('span', { class: 'dp-5-cross' }, '✕'),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 06 — Dark · Neon
// =============================================================

function renderDesign6(): HTMLElement {
  const root = el('div', { class: 'dp-6' });
  root.append(
    el('div', { class: 'dp-6-grid-bg', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-6-glow', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-6-content' },
      el('div', { class: 'dp-6-status' },
        el('span', { class: 'dp-6-status-dot' }),
        el('span', { class: 'dp-6-status-text' }, 'system: ready · 0 cookies · 0 leaks'),
      ),
      el('h2', { class: 'dp-6-title' },
        el('span', { class: 'dp-6-title-prefix' }, 'gotyou'),
        el('span', { class: 'dp-6-title-sep' }, ':/'),
        el('span', { class: 'dp-6-title-app' }, 'Slot'),
      ),
      el('p', { class: 'dp-6-tagline' },
        'Visueller Terminfinder. Im Browser. ',
        el('span', { class: 'dp-6-accent' }, 'Privacy by default.'),
      ),
      el('div', { class: 'dp-6-cta-row' },
        el('a', { href: '/new', class: 'dp-6-cta' }, '> umfrage_starten()'),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 07 — Glas · Frosted
// =============================================================

function renderDesign7(): HTMLElement {
  const root = el('div', { class: 'dp-7' });
  // Floating colour blobs behind the glass card.
  root.append(
    el('div', { class: 'dp-7-blob dp-7-blob-1', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-7-blob dp-7-blob-2', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-7-blob dp-7-blob-3', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-7-card' },
      el('span', { class: 'dp-7-eyebrow' }, '✨ gotyou : Slot'),
      el('h2', { class: 'dp-7-title' }, 'Termine, durchsichtig.'),
      el('p', { class: 'dp-7-tagline' },
        'Ein Terminfinder, der nichts versteckt — und nichts speichert.',
      ),
      el('a', { href: '/new', class: 'dp-7-cta' }, 'Jetzt loslegen'),
    ),
  );
  return root;
}

// =============================================================
// Design 08 — Notion · Dokument
// =============================================================

function renderDesign8(): HTMLElement {
  const root = el('div', { class: 'dp-8' });
  root.append(
    el('div', { class: 'dp-8-doc' },
      el('div', { class: 'dp-8-breadcrumb' }, 'gotyou / slot / readme'),
      el('h2', { class: 'dp-8-title' },
        el('span', { class: 'dp-8-emoji' }, '🗓️'),
        ' Slot',
      ),
      el('p', { class: 'dp-8-meta' },
        el('span', {}, 'Tool · Terminfindung'),
        el('span', { class: 'dp-8-dot' }, '·'),
        el('span', {}, 'erstellt 2026'),
        el('span', { class: 'dp-8-dot' }, '·'),
        el('span', {}, 'kein Account'),
      ),
      el('hr', { class: 'dp-8-rule' }),
      el('p', { class: 'dp-8-paragraph' },
        'Slot ist ein visueller Terminfinder. Du baust eine Matrix aus Tagen ',
        'und Zeitslots, schickst einen Link an deine Leute, und siehst sofort, ',
        'wann es bei den meisten passt.',
      ),
      el('div', { class: 'dp-8-callout' },
        el('span', { class: 'dp-8-callout-emoji' }, '🔒'),
        el('span', {},
          el('strong', {}, 'Privacy: '),
          'Kalender bleibt im Browser. Pseudonyme statt Namen. Auto-Löschung.',
        ),
      ),
      el('a', { href: '/new', class: 'dp-8-cta' },
        '/loslegen ',
        el('span', { class: 'dp-8-cta-arrow' }, '→'),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 09 — Bauhaus · Geometrisch
// =============================================================

function renderDesign9(): HTMLElement {
  const root = el('div', { class: 'dp-9' });
  root.append(
    el('div', { class: 'dp-9-shape dp-9-circle', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-9-shape dp-9-triangle', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-9-shape dp-9-square', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-9-shape dp-9-line', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-9-content' },
      el('div', { class: 'dp-9-mark' }, 'gotyou'),
      el('h2', { class: 'dp-9-title' },
        el('span', {}, 'S'),
        el('span', { class: 'dp-9-title-mid' }, 'L'),
        el('span', {}, 'O'),
        el('span', { class: 'dp-9-title-mid' }, 'T'),
      ),
      el('p', { class: 'dp-9-rule' }),
      el('p', { class: 'dp-9-tagline' },
        'Form folgt Funktion. Termin folgt Klick.',
      ),
      el('a', { href: '/new', class: 'dp-9-cta' }, 'Anfangen'),
    ),
  );
  return root;
}

// =============================================================
// Design 10 — Memphis · 80er
// =============================================================

function renderDesign10(): HTMLElement {
  const root = el('div', { class: 'dp-10' });
  root.append(
    el('div', { class: 'dp-10-pattern', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-10-blob dp-10-blob-1', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-10-blob dp-10-blob-2', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-10-content' },
      el('span', { class: 'dp-10-eyebrow' }, '★ gotyou : Slot ★'),
      el('h2', { class: 'dp-10-title' },
        el('span', { class: 'dp-10-t1' }, 'TER'),
        el('span', { class: 'dp-10-t2' }, 'MIN'),
        el('span', { class: 'dp-10-t3' }, 'PARTY'),
      ),
      el('p', { class: 'dp-10-tagline' },
        'Wer hat Zeit? Du klickst, der Server klatscht in die Hände.',
      ),
      el('a', { href: '/new', class: 'dp-10-cta' }, 'LET\'S GO ▶'),
    ),
  );
  return root;
}

// =============================================================
// Design 11 — Riso · Druck
// =============================================================

function renderDesign11(): HTMLElement {
  const root = el('div', { class: 'dp-11' });
  root.append(
    el('div', { class: 'dp-11-grain', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-11-content' },
      el('div', { class: 'dp-11-frame' },
        el('span', { class: 'dp-11-eyebrow' }, 'Druckwerkstatt · gotyou.at'),
        el('h2', { class: 'dp-11-title' },
          el('span', { class: 'dp-11-title-shadow' }, 'Slot'),
          el('span', { class: 'dp-11-title-front' }, 'Slot'),
        ),
        el('p', { class: 'dp-11-tagline' },
          'Eine zweifarbige Edition. Keine Daten. Limitiert auf jetzt.',
        ),
        el('a', { href: '/new', class: 'dp-11-cta' }, 'Edition starten ›'),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 12 — Schreibmaschine
// =============================================================

function renderDesign12(): HTMLElement {
  const root = el('div', { class: 'dp-12' });
  root.append(
    el('div', { class: 'dp-12-paper' },
      el('div', { class: 'dp-12-header' },
        el('span', {}, 'gotyou.at / slot'),
        el('span', {}, 'No. 001'),
      ),
      el('h2', { class: 'dp-12-title' },
        'SLOT.',
        el('span', { class: 'dp-12-cursor', 'aria-hidden': 'true' }, '▌'),
      ),
      el('div', { class: 'dp-12-line' }, 'Ein Werkzeug zur Findung gemeinsamer Termine.'),
      el('div', { class: 'dp-12-line' }, 'Ohne Speicherung des Privaten. Ohne Konto.'),
      el('div', { class: 'dp-12-line' }, 'Ohne Newsletter. Ohne Tinder-Bezug. Ohne Drama.'),
      el('div', { class: 'dp-12-spacer' }),
      el('a', { href: '/new', class: 'dp-12-cta' }, '▮  UMFRAGE ANLEGEN'),
    ),
  );
  return root;
}

// =============================================================
// Design 13 — Terminal · Phosphor
// =============================================================

function renderDesign13(): HTMLElement {
  const root = el('div', { class: 'dp-13' });
  root.append(
    el('div', { class: 'dp-13-screen' },
      el('div', { class: 'dp-13-scanlines', 'aria-hidden': 'true' }),
      el('div', { class: 'dp-13-content' },
        el('div', { class: 'dp-13-line' }, '> connect gotyou.at:slot'),
        el('div', { class: 'dp-13-line' }, '  authenticated as: anonymous'),
        el('div', { class: 'dp-13-line' }, '  cookies set: 0'),
        el('div', { class: 'dp-13-line' }, '  identity stored: 0'),
        el('div', { class: 'dp-13-line dp-13-empty' }),
        el('h2', { class: 'dp-13-title' }, 'SLOT'),
        el('div', { class: 'dp-13-line' },
          '  ',
          el('span', { class: 'dp-13-prompt' }, '▶'),
          ' visual scheduler. drag to plan. share via link.',
        ),
        el('div', { class: 'dp-13-line dp-13-empty' }),
        el('a', { href: '/new', class: 'dp-13-cta' },
          '> ./start_survey.sh',
          el('span', { class: 'dp-13-cursor', 'aria-hidden': 'true' }, '_'),
        ),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 14 — Zeitung · Letterpress
// =============================================================

function renderDesign14(): HTMLElement {
  const root = el('div', { class: 'dp-14' });
  root.append(
    el('div', { class: 'dp-14-paper' },
      el('div', { class: 'dp-14-masthead' }, 'THE  GOTYOU  GAZETTE'),
      el('div', { class: 'dp-14-dateline' },
        el('span', {}, 'WIEN · ÖSTERREICH'),
        el('span', { class: 'dp-14-dot' }, '◆'),
        el('span', {}, 'PRIVATE EDITION'),
        el('span', { class: 'dp-14-dot' }, '◆'),
        el('span', {}, 'No. I'),
      ),
      el('div', { class: 'dp-14-rule' }),
      el('div', { class: 'dp-14-cols' },
        el('div', { class: 'dp-14-col-main' },
          el('h2', { class: 'dp-14-headline' }, 'SLOT.'),
          el('p', { class: 'dp-14-deck' },
            'Wie ein einfaches Werkzeug die Mühen der Terminfindung beendet — ',
            'und dabei kein einziges Datum verrät.',
          ),
          el('p', { class: 'dp-14-paragraph' },
            el('span', { class: 'dp-14-dropcap' }, 'M'),
            'an klickt eine Matrix an, schickt einen Link, ',
            'und wartet. Der Rest erledigt sich, ohne dass irgendjemand ',
            'eine E-Mail-Adresse, ein Passwort oder einen Hauch ',
            'von Identität preisgibt.',
          ),
          el('a', { href: '/new', class: 'dp-14-cta' }, 'Lesen Sie weiter →'),
        ),
        el('div', { class: 'dp-14-col-side' },
          el('div', { class: 'dp-14-pull' },
            el('span', { class: 'dp-14-quote' }, '“'),
            el('p', {}, 'Ich habe das Werkzeug benutzt, fünf Termine in einer Minute, kein Drama.'),
            el('p', { class: 'dp-14-attr' }, '— Eine Person, die es ausprobiert hat'),
          ),
        ),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 15 — Karte · Vintage
// =============================================================

function renderDesign15(): HTMLElement {
  const root = el('div', { class: 'dp-15' });
  root.append(
    el('div', { class: 'dp-15-paper' },
      el('div', { class: 'dp-15-compass', 'aria-hidden': 'true' },
        el('div', { class: 'dp-15-compass-N' }, 'N'),
        el('div', { class: 'dp-15-compass-S' }, 'S'),
        el('div', { class: 'dp-15-compass-E' }, 'E'),
        el('div', { class: 'dp-15-compass-W' }, 'W'),
        el('div', { class: 'dp-15-compass-star' }),
      ),
      el('div', { class: 'dp-15-content' },
        el('p', { class: 'dp-15-eyebrow' }, '— Cartographia gotyou —'),
        el('h2', { class: 'dp-15-title' }, 'Slot.'),
        el('p', { class: 'dp-15-coords' }, '48°12′N · 16°22′E · MMXXVI'),
        el('p', { class: 'dp-15-tagline' },
          'Eine Karte deiner möglichen Termine. ',
          'Du wählst die Route, deine Begleiter sagen, wo sie aussteigen.',
        ),
        el('a', { href: '/new', class: 'dp-15-cta' }, '⌖ Reise planen'),
      ),
    ),
  );
  return root;
}

// =============================================================
// Design 16 — Origami · Paper
// =============================================================

function renderDesign16(): HTMLElement {
  const root = el('div', { class: 'dp-16' });
  root.append(
    el('div', { class: 'dp-16-fold dp-16-fold-1', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-16-fold dp-16-fold-2', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-16-fold dp-16-fold-3', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-16-content' },
      el('span', { class: 'dp-16-eyebrow' }, 'gotyou : Slot'),
      el('h2', { class: 'dp-16-title' },
        el('span', { class: 'dp-16-fold-letter' }, 'S'),
        el('span', { class: 'dp-16-fold-letter' }, 'l'),
        el('span', { class: 'dp-16-fold-letter' }, 'o'),
        el('span', { class: 'dp-16-fold-letter' }, 't'),
      ),
      el('p', { class: 'dp-16-tagline' },
        'Termine, gefaltet aus einem einzigen Blatt Papier. ',
        el('br'),
        'Keine zusätzlichen Daten nötig.',
      ),
      el('a', { href: '/new', class: 'dp-16-cta' }, 'Falten beginnen'),
    ),
  );
  return root;
}

// =============================================================
// Design 17 — Living Matrix · die App demonstriert sich selbst
// =============================================================

function renderDesign17(): HTMLElement {
  const root = el('div', { class: 'dp-17' });
  // Build a 7x10 mini-matrix that auto-fills with simulated votes.
  const grid = el('div', { class: 'dp-17-matrix' });
  const days = 7, slots = 10;
  const cells: HTMLElement[] = [];
  for (let d = 0; d < days; d++) {
    for (let s = 0; s < slots; s++) {
      const cell = el('span', { class: 'dp-17-cell' });
      cells.push(cell);
      grid.append(cell);
    }
  }
  // Animate cells filling with a probability distribution to simulate "voting".
  setInterval(() => {
    const idx = Math.floor(Math.random() * cells.length);
    const cell = cells[idx];
    const score = Math.random();
    cell.className = 'dp-17-cell ' +
      (score > 0.7 ? 'dp-17-yes' :
       score > 0.4 ? 'dp-17-maybe' :
       score > 0.1 ? 'dp-17-no' : 'dp-17-empty');
  }, 250);

  root.append(
    el('div', { class: 'dp-17-pane dp-17-demo' },
      el('div', { class: 'dp-17-demo-label' }, 'live: 26 stimmen heute · keine namen ↗'),
      grid,
    ),
    el('div', { class: 'dp-17-pane dp-17-text' },
      el('span', { class: 'dp-17-eyebrow' }, 'gotyou : Slot'),
      el('h2', { class: 'dp-17-title' }, 'Schau zu, wie es funktioniert.'),
      el('p', { class: 'dp-17-tagline' },
        'Du siehst gerade eine Live-Simulation. Genau so passiert es bei dir, ',
        'sobald du den Link rumschickst — nur eben mit echten Stimmen.',
      ),
      el('a', { href: '/new', class: 'dp-17-cta' }, 'Eigene Umfrage starten →'),
    ),
  );
  return root;
}

// =============================================================
// Design 18 — Magnetic Type · Buchstaben folgen dem Cursor
// =============================================================

function renderDesign18(): HTMLElement {
  const root = el('div', { class: 'dp-18' });
  const title = el('h2', { class: 'dp-18-title' });
  const word = 'Slot.';
  const letters: HTMLElement[] = [];
  for (const ch of word) {
    const letter = el('span', { class: 'dp-18-letter' }, ch === ' ' ? ' ' : ch);
    title.append(letter);
    letters.push(letter);
  }

  root.addEventListener('pointermove', (e) => {
    const rect = root.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    for (const letter of letters) {
      const lr = letter.getBoundingClientRect();
      const lx = lr.left + lr.width / 2 - rect.left;
      const ly = lr.top + lr.height / 2 - rect.top;
      const dx = cx - lx, dy = cy - ly;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const pull = Math.min(20, 800 / Math.max(50, dist));
      const angle = Math.atan2(dy, dx);
      letter.style.transform =
        `translate(${Math.cos(angle) * pull}px, ${Math.sin(angle) * pull}px)`;
    }
  });
  root.addEventListener('pointerleave', () => {
    for (const letter of letters) letter.style.transform = '';
  });

  root.append(
    el('span', { class: 'dp-18-eyebrow' }, '↳ Beweg den Cursor über den Titel'),
    title,
    el('p', { class: 'dp-18-tagline' },
      'Slot reagiert. Du auch — gleich nach dem Klick.',
    ),
    el('a', { href: '/new', class: 'dp-18-cta' }, 'Termine planen'),
  );
  return root;
}

// =============================================================
// Design 19 — Word Carousel · cycling synonyms morph into "Slot"
// =============================================================

function renderDesign19(): HTMLElement {
  const root = el('div', { class: 'dp-19' });
  const cycler = el('span', { class: 'dp-19-cycle' }, 'Termine');
  const words = ['Termine', 'Treffen', 'Zusagen', 'Stunden', 'Slots'];
  let i = 0;
  setInterval(() => {
    i = (i + 1) % words.length;
    cycler.classList.add('is-fading');
    setTimeout(() => {
      cycler.textContent = words[i];
      cycler.classList.remove('is-fading');
    }, 280);
  }, 1800);

  root.append(
    el('div', { class: 'dp-19-content' },
      el('p', { class: 'dp-19-eyebrow' }, '— gotyou präsentiert —'),
      el('h2', { class: 'dp-19-title' },
        el('span', { class: 'dp-19-static' }, 'Plane '),
        cycler,
        el('span', { class: 'dp-19-static' }, '.'),
      ),
      el('p', { class: 'dp-19-final' },
        'Egal wie du sie nennst — ',
        el('strong', {}, 'Slot'),
        ' findet sie.',
      ),
      el('a', { href: '/new', class: 'dp-19-cta' }, 'Loslegen'),
    ),
  );
  return root;
}

// =============================================================
// Design 20 — Mesh · Cinematic
// =============================================================

function renderDesign20(): HTMLElement {
  const root = el('div', { class: 'dp-20' });
  root.append(
    el('div', { class: 'dp-20-mesh', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-20-vignette', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-20-content' },
      el('p', { class: 'dp-20-eyebrow' }, 'gotyou : Slot'),
      el('h2', { class: 'dp-20-title' }, 'Die ruhigste Art,\nTermine zu finden.'),
      el('p', { class: 'dp-20-tagline' },
        'Keine Werbung. Keine Konten. Keine Datenspuren.',
        el('br'),
        'Nur du, ein Link, eine Antwort.',
      ),
      el('a', { href: '/new', class: 'dp-20-cta' }, 'Anfangen'),
    ),
  );
  return root;
}

// =============================================================
// Design 21 — Heatmap Wordmark · die Buchstaben sind Daten
// =============================================================

function renderDesign21(): HTMLElement {
  const root = el('div', { class: 'dp-21' });
  // Each letter rendered as a 5-col × 7-row tiny grid of cells.
  // Pre-baked patterns (true/false per cell) for S L O T.
  const patterns: Record<string, string[]> = {
    S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
    L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
    O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
    T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  };
  const wordmark = el('div', { class: 'dp-21-wordmark' });
  const allCells: HTMLElement[] = [];
  for (const ch of 'SLOT') {
    const letter = el('div', { class: 'dp-21-letter' });
    const rows = patterns[ch];
    for (const row of rows) {
      for (const c of row) {
        const cell = el('span', {
          class: c === '#' ? 'dp-21-cell dp-21-on' : 'dp-21-cell dp-21-off',
        });
        letter.append(cell);
        if (c === '#') allCells.push(cell);
      }
    }
    wordmark.append(letter);
  }
  // Animate scoring: cycle "active" cells to simulate vote arrival.
  setInterval(() => {
    const idx = Math.floor(Math.random() * allCells.length);
    const score = Math.random();
    const cell = allCells[idx];
    cell.classList.remove('hue-yes', 'hue-maybe', 'hue-no');
    cell.classList.add(score > 0.66 ? 'hue-yes' : score > 0.33 ? 'hue-maybe' : 'hue-no');
  }, 180);

  root.append(
    wordmark,
    el('p', { class: 'dp-21-tagline' },
      'Die Buchstaben sind eine Heat-Map. Die Heat-Map ist das Logo. ',
      el('br'),
      'Das Tool ist beides.',
    ),
    el('a', { href: '/new', class: 'dp-21-cta' }, 'Heat-Map starten →'),
  );
  return root;
}

// =============================================================
// Design 22 — Live Time · Hero kennt die Uhrzeit
// =============================================================

function renderDesign22(): HTMLElement {
  const root = el('div', { class: 'dp-22' });
  const clock = el('div', { class: 'dp-22-clock' });
  const date = el('div', { class: 'dp-22-date' });
  const tick = () => {
    const now = new Date();
    const h = String(now.getHours()).padStart(2, '0');
    const m = String(now.getMinutes()).padStart(2, '0');
    const s = String(now.getSeconds()).padStart(2, '0');
    clock.textContent = `${h}:${m}`;
    const wd = ['Sonntag','Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag'][now.getDay()];
    const dd = String(now.getDate()).padStart(2, '0');
    const mo = String(now.getMonth() + 1).padStart(2, '0');
    date.textContent = `${wd}, ${dd}.${mo}. · ${s}s`;
  };
  tick();
  setInterval(tick, 1000);

  root.append(
    el('div', { class: 'dp-22-content' },
      el('p', { class: 'dp-22-eyebrow' }, 'jetzt gerade in wien'),
      clock,
      date,
      el('h2', { class: 'dp-22-title' }, 'Slot.'),
      el('p', { class: 'dp-22-tagline' },
        'Nicht der nächste Kalender. Der ',
        el('em', {}, 'leiseste'),
        '. ',
        'Termine finden, ohne dass jemand mitschreibt.',
      ),
      el('a', { href: '/new', class: 'dp-22-cta' }, 'Termine fixieren'),
    ),
  );
  return root;
}

// =============================================================
// Design 23 — Conversational · Chat-style Reveal
// =============================================================

function renderDesign23(): HTMLElement {
  const root = el('div', { class: 'dp-23' });
  const stream = el('div', { class: 'dp-23-stream' });
  const lines: Array<[string, 'q' | 'a']> = [
    ['Wann passt es bei euch allen?', 'q'],
    ['Mo? Di? Egal …', 'q'],
    ['Ich hab keine Lust auf Doodle.', 'q'],
    ['Slot: ich finde es heraus.', 'a'],
    ['Kein Account. Kein Tracking. Ein Link.', 'a'],
  ];

  // Reveal lines one after another.
  let idx = 0;
  const reveal = () => {
    if (idx >= lines.length) return;
    const [text, kind] = lines[idx++];
    const bubble = el('div', { class: `dp-23-bubble dp-23-${kind}` }, text);
    stream.append(bubble);
    setTimeout(reveal, kind === 'a' ? 1100 : 700);
  };
  setTimeout(reveal, 400);

  root.append(
    stream,
    el('div', { class: 'dp-23-cta-row' },
      el('a', { href: '/new', class: 'dp-23-cta' }, 'Ja, los →'),
    ),
  );
  return root;
}

// =============================================================
// Design 24 — 3D Tilt · Cursor-tracking Depth
// =============================================================

function renderDesign24(): HTMLElement {
  const root = el('div', { class: 'dp-24' });
  const stage = el('div', { class: 'dp-24-stage' });
  const card = el('div', { class: 'dp-24-card' });
  const back = el('div', { class: 'dp-24-back', 'aria-hidden': 'true' });
  const front = el('div', { class: 'dp-24-front' },
    el('span', { class: 'dp-24-eyebrow' }, 'gotyou : Slot'),
    el('h2', { class: 'dp-24-title' }, 'Tiefe.'),
    el('p', { class: 'dp-24-tagline' },
      'Termine in drei Dimensionen denken: Tag, Zeit, Verfügbarkeit. ',
      'Cursor bewegen — die Karte folgt.',
    ),
    el('a', { href: '/new', class: 'dp-24-cta' }, 'Eintauchen'),
  );
  card.append(back, front);
  stage.append(card);

  stage.addEventListener('pointermove', (e) => {
    const r = stage.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    card.style.transform = `rotateY(${x * 14}deg) rotateX(${-y * 14}deg)`;
    back.style.transform = `translate(${x * -30}px, ${y * -30}px)`;
  });
  stage.addEventListener('pointerleave', () => {
    card.style.transform = '';
    back.style.transform = '';
  });

  root.append(stage);
  return root;
}

// =============================================================
// Design 25 — Variable Font · Wave (Hover-Welle durch die Buchstaben)
// =============================================================

function renderDesign25(): HTMLElement {
  const root = el('div', { class: 'dp-25' });
  const word = 'gotyou:Slot';
  const title = el('h2', { class: 'dp-25-title' });
  for (const ch of word) {
    const letter = el('span', { class: 'dp-25-letter' }, ch === ' ' ? ' ' : ch);
    title.append(letter);
  }

  root.append(
    el('span', { class: 'dp-25-eyebrow' }, 'fahre über den titel'),
    title,
    el('p', { class: 'dp-25-tagline' },
      'Slot ist nicht starr. Genau wie deine Termine. ',
      'Hover ist nur der Anfang.',
    ),
    el('a', { href: '/new', class: 'dp-25-cta' }, 'In Bewegung kommen'),
  );
  return root;
}

// =============================================================
// Design 26 — Aurora · Liquid · langsam fließende Farbschichten
// =============================================================

function renderDesign26(): HTMLElement {
  const root = el('div', { class: 'dp-26' });
  root.append(
    el('div', { class: 'dp-26-aurora dp-26-aurora-1', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-26-aurora dp-26-aurora-2', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-26-aurora dp-26-aurora-3', 'aria-hidden': 'true' }),
    el('div', { class: 'dp-26-content' },
      el('p', { class: 'dp-26-eyebrow' }, '— still und stetig —'),
      el('h2', { class: 'dp-26-title' }, 'Slot.'),
      el('p', { class: 'dp-26-tagline' },
        'Termine wie Polarlichter:',
        el('br'),
        'da, sobald die Bedingungen stimmen.',
      ),
      el('a', { href: '/new', class: 'dp-26-cta' }, 'Bedingungen schaffen'),
    ),
  );
  return root;
}
